/**
 * Assistant turn — no avatar, no role marker, flush to the column.
 * Visual distinction comes from the absence of the grey
 * :class:`UserPrompt` background around it. Blocks (text, tool use,
 * thinking, image, document) flow continuously in the order Claude
 * emitted them.
 *
 * Vertical rhythm is dead-simple on purpose: every visible block
 * sits in a flex column with a single uniform gap. Tool rows,
 * thinking markers, and prose paragraphs share the same beat so
 * the eye reads a tool sequence as part of the agent's narration
 * rather than a side-channel ledger jammed between paragraphs.
 *
 * No "agent is working" placeholder lives in this component. Transcript
 * events replace/append here; the footer-level working marker is rendered
 * separately by :class:`TurnStream`.
 */

import { formatStopReason, formatTurnError } from '@/lib/agent/errors';
import type { ActiveTask, SubagentState, Turn } from '@/lib/agent/reducer';
import type { ContentBlock, ToolResultBlock } from '@/lib/agent/types';
import { cn } from '@/lib/utils/cn';
import { BlockRouter } from './blocks';
import { TurnNoticePill } from './TurnNoticePill';

/**
 * Block types that render nothing in :class:`BlockRouter`. We only
 * silence ``ToolResultBlock`` here — those are surfaced inside their
 * paired tool row and never on their own. Everything else (text,
 * thinking, tool, image, document, code-execution / web-search
 * results) carries its own weight in the column.
 */
const SILENCED_BLOCK_TYPES = new Set<ContentBlock['block_type']>(['ToolResultBlock']);
const THINKING_TEXT_HANDOFF_MIN_CHARS = 24;

export interface AssistantStreamProps {
  turn: Turn;
  getToolResult: (toolUseId: string) => ToolResultBlock | undefined;
  getSubagent?: (toolUseId: string) => SubagentState | undefined;
  getActiveTask?: (toolUseId: string) => ActiveTask | undefined;
  getToolPending?: (toolUseId: string) => boolean;
  sessionId?: string | null;
  onStopTask?: (taskId: string) => void;
  onLayoutShift?: () => void;
  keepThinkingVisible?: boolean;
  className?: string;
}

export function AssistantStream({
  turn,
  getToolResult,
  getSubagent,
  getActiveTask,
  getToolPending,
  sessionId,
  onStopTask,
  onLayoutShift,
  keepThinkingVisible = false,
  className,
}: AssistantStreamProps) {
  const streaming = !turn.committed;

  // Walk ``turn.order`` directly so block keys stay welded to their
  // *source* index in ``turn.blocks``. Earlier we iterated a
  // filtered array and keyed by the filtered position — that meant
  // any silenced or transiently-undefined block in the middle would
  // shift the keys of every block after it, remounting the tool
  // rows / fading text back in even though the underlying content
  // hadn't changed. The source index is monotonically appended by
  // the SSE reducer and never reused, so it's the stable id we want.
  const renderable: { block: ContentBlock; sourceIndex: number }[] = [];
  const heldThinkingIndex = findHeldThinkingIndex(turn, keepThinkingVisible);
  for (const sourceIndex of turn.order) {
    const block = turn.blocks[sourceIndex];
    if (!isRenderableBlock(block)) continue;
    if (heldThinkingIndex !== null) {
      if (block.block_type === 'ThinkingBlock' && sourceIndex !== heldThinkingIndex) continue;
    } else if (block.block_type === 'ThinkingBlock') {
      continue;
    }
    renderable.push({ block, sourceIndex });
  }

  // Map raw stop / error codes to designed notices once per render.
  // ``null`` is the "nothing to surface" signal — the formatters
  // own the policy of which stops/errors are worth showing.
  const errorNotice = formatTurnError(turn.error);
  const stopNotice = formatStopReason(turn.stopReason);
  if (renderable.length === 0 && !errorNotice && !stopNotice) {
    return null;
  }

  // Identify the last renderable position so the streaming flag
  // sits on whichever block is currently growing.
  const lastIdx = renderable.length - 1;
  return (
    <div className={cn('flex w-full max-w-[680px] flex-col gap-2 text-[14px] leading-relaxed', className)}>
      {renderable.map(({ block, sourceIndex }, i) => (
        <div
          key={`${turn.id}-${sourceIndex}`}
          className={cn(
            // ``animate-in`` plays once on the render where the
            // class first appears. Gated on the turn being
            // uncommitted (``streaming``) so blocks loaded from
            // history -- which mount all-at-once -- never
            // flurry-animate when the user reopens a session or
            // pages older messages. Each block within a live
            // streaming turn first mounts the moment the SSE
            // delta makes it renderable, and the class lands at
            // that mount, so the bubble slides up by 8px and
            // fades in over 280ms. After the turn commits the
            // class drops off harmlessly -- the animation has
            // long completed and ``animation-fill-mode: backwards``
            // already returned the element to its natural state.
            '[&:empty]:hidden',
            streaming && 'fade-in-0 slide-in-from-bottom-2 animate-in duration-300 ease-out'
          )}
        >
          <BlockRouter
            block={block}
            streaming={streaming && i === lastIdx}
            partialJson={turn.partialToolInput[sourceIndex]}
            timing={turn.blockTimings?.[sourceIndex]}
            getToolResult={getToolResult}
            getSubagent={getSubagent}
            getActiveTask={getActiveTask}
            getToolPending={getToolPending}
            sessionId={sessionId}
            onStopTask={onStopTask}
            // ``onLayoutShift`` is plumbed to every block, not just
            // the streaming tail. A tool block's body can open well
            // after it stopped being the trailing block -- when the
            // ``task_started`` SDK frame lands and
            // ``activeTaskForRunLine`` flips defined, the grid
            // expands ~150-200px synchronously and the chat needs
            // to pin in the same commit. Restricting to
            // ``streaming && i === lastIdx`` left this case to the
            // RO alone, which lagged a frame and dropped the
            // reader off the floor when the subagent feed
            // appeared.
            onLayoutShift={onLayoutShift}
          />
        </div>
      ))}
      {/* Error pill takes priority over stop reason — if the turn
          carries both (e.g. a refusal that also surfaces as
          ``invalid_request``) the error is the more useful signal. */}
      {errorNotice && <TurnNoticePill notice={errorNotice} />}
      {!errorNotice && stopNotice && <TurnNoticePill notice={stopNotice} />}
    </div>
  );
}

function findHeldThinkingIndex(turn: Turn, keepCommittedThinkingVisible: boolean): number | null {
  if (turn.committed && !keepCommittedThinkingVisible) return null;

  let held: number | null = null;
  for (const [position, sourceIndex] of turn.order.entries()) {
    const block = turn.blocks[sourceIndex];
    if (block?.block_type !== 'ThinkingBlock') continue;
    const replacement = findNextRenderableBlock(turn, position);
    if (replacement === null || !isReadyToReplaceThinking(turn, replacement)) {
      held = sourceIndex;
    }
  }
  return held;
}

function findNextRenderableBlock(
  turn: Turn,
  afterPosition: number
): { block: ContentBlock; sourceIndex: number } | null {
  for (const sourceIndex of turn.order.slice(afterPosition + 1)) {
    const block = turn.blocks[sourceIndex];
    if (!isRenderableBlock(block)) continue;
    if (block.block_type === 'ThinkingBlock') return null;
    return { block, sourceIndex };
  }
  return null;
}

function isRenderableBlock(block: ContentBlock | undefined): block is ContentBlock {
  if (!block || SILENCED_BLOCK_TYPES.has(block.block_type)) return false;
  if (block.block_type === 'RedactedThinkingBlock') return false;
  if (block.block_type === 'TextBlock' && (block.text ?? '').length === 0) return false;
  return true;
}

function isReadyToReplaceThinking(turn: Turn, candidate: { block: ContentBlock; sourceIndex: number }): boolean {
  if (candidate.block.block_type === 'TextBlock') {
    return (candidate.block.text ?? '').trim().length >= THINKING_TEXT_HANDOFF_MIN_CHARS;
  }
  if (candidate.block.block_type === 'ToolUseBlock' || candidate.block.block_type === 'ServerToolUseBlock') {
    return !!candidate.block.name || turn.partialToolInput[candidate.sourceIndex] !== undefined;
  }
  return true;
}
